export const USER_STATUS = ['active', 'inactive'] as const;

export const USER_TYPE = {
  SA: 1,
  ADM: 2,
  HOD: 3,
  ASM: 4,
  TSO: 5,
  AGENT: 6,
  GENERAL: 7,
  RSM: 8,
  CMO: 9,
} as const;

export const NON_HIERARCHICAL_USER_TYPES: ReadonlySet<number> = new Set([
  USER_TYPE.SA,
  USER_TYPE.ADM,
  USER_TYPE.GENERAL,
]);

export const CLEARABLE_LOOKUP_FIELDS = ['companyId', 'departmentId', 'designationId'] as const;

export const CLEARABLE_TEXT_FIELDS = ['email'] as const;

export const DATE_USER_FIELDS = ['activeFrom', 'activeTo'] as const;

export type DateUserField = (typeof DATE_USER_FIELDS)[number];

export function isDateUserField(field: string): field is DateUserField {
  return (DATE_USER_FIELDS as readonly string[]).includes(field);
}

export const USER_TYPE_GROUPS = {
  admin: [USER_TYPE.SA, USER_TYPE.ADM],
  agent: [USER_TYPE.AGENT],
  general: [USER_TYPE.GENERAL],
  marketing: [USER_TYPE.ASM, USER_TYPE.TSO],
  special: [USER_TYPE.HOD, USER_TYPE.RSM, USER_TYPE.CMO],
} as const;

export type UserTypeGroup = keyof typeof USER_TYPE_GROUPS;

import type { CreateUserPayload } from '../types/user.types';

export const COMMON_USER_FIELDS: readonly (keyof CreateUserPayload)[] = [
  'fullName',
  'username',
  'hrEmpId',
  'mobile',
  'email',
];

export const LOOKUP_USER_FIELDS = [
  'companyId',
  'departmentId',
  'designationId',
  'geoUnitId',
  'parentUserId',
] as const;
export type LookupUserField = (typeof LOOKUP_USER_FIELDS)[number];

export const NUMERIC_USER_FIELDS: ReadonlySet<string> = new Set([
  'mobile',
  'accountNumber',
  'contactNo',
]);

export function isNumericUserField(field: string): boolean {
  return NUMERIC_USER_FIELDS.has(field);
}

export const REQUIRED_USER_FIELDS: ReadonlySet<string> = new Set([
  'fullName',
  'username',
  'hrEmpId',
  'password',
  'mobile',
  'userTypeId',
]);

export function isRequiredUserField(field: string): boolean {
  return REQUIRED_USER_FIELDS.has(field);
}

export function isGeoUnitRequired(userTypeId: number | undefined): boolean {
  return userTypeId !== undefined;
}

export const USER_FIELD_MAX_LENGTHS: Record<string, number> = {
  fullName: 50,
  username: 25,
  hrEmpId: 20,
  mobile: 11,
  contactNo: 11,
  accountNumber: 20,
  nickname: 25,
  address: 150,
};

export const USER_FIELD_LABELS: Record<string, string> = {
  fullName: 'Full Name',
  username: 'Username',
  hrEmpId: 'Employee ID',
  password: 'Password',
  mobile: 'Mobile',
  email: 'Email',
  companyId: 'Company',
  departmentId: 'Department',
  designationId: 'Designation',
  parentUserId: 'Reporting Manager',
  accountNumber: 'Account Number',
  nickname: 'Nickname',
  contactNo: 'Alternate Contact',
  address: 'Address',
  activeFrom: 'Active From',
  activeTo: 'Active To',
};

export function getUserFieldLabel(field: string): string {
  return USER_FIELD_LABELS[field] ?? field;
}

export const CATEGORY_USER_FIELDS: Record<UserTypeGroup, readonly (keyof CreateUserPayload)[]> = {
  admin: [
    'companyId',
    'departmentId',
    'designationId',
    'parentUserId',
    'geoUnitId',
    'accountNumber',
    'nickname',
    'contactNo',
    'address',
    'activeFrom',
    'activeTo',
  ],
  agent: [
    'companyId',
    'departmentId',
    'designationId',
    'parentUserId',
    'geoUnitId',
    'accountNumber',
    'nickname',
    'contactNo',
    'address',
    'activeFrom',
    'activeTo',
  ],
  general: [
    'companyId',
    'departmentId',
    'designationId',
    'parentUserId',
    'geoUnitId',
    'accountNumber',
    'nickname',
    'contactNo',
    'address',
    'activeFrom',
    'activeTo',
  ],
  marketing: [
    'companyId',
    'departmentId',
    'designationId',
    'parentUserId',
    'geoUnitId',
    'accountNumber',
    'nickname',
    'contactNo',
    'address',
    'activeFrom',
    'activeTo',
  ],
  special: [
    'companyId',
    'departmentId',
    'designationId',
    'parentUserId',
    'geoUnitId',
    'accountNumber',
    'nickname',
    'contactNo',
    'address',
    'activeFrom',
    'activeTo',
  ],
};

export function isLookupUserField(field: string): field is LookupUserField {
  return (LOOKUP_USER_FIELDS as readonly string[]).includes(field);
}

export const USER_CATEGORY_ROUTES: Record<UserTypeGroup, string> = {
  admin: '/users/admin',
  agent: '/users/agent',
  general: '/users/general',
  marketing: '/users/marketing',
  special: '/users/special',
};

export const USER_CATEGORY_TITLES: Record<UserTypeGroup, string> = {
  admin: 'Admin Users',
  agent: 'Agent Users',
  general: 'General Users',
  marketing: 'Marketing Users',
  special: 'Special Users',
};
